import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Payment status derived from ledger: Unpaid / Deposit Paid / Fully Paid
export function paymentStatus(order) {
  const total = (order?.total_amount || 0) + (order?.delivery_fee || 0) - (order?.discount || 0);
  const paid = order?.deposit_paid || 0;
  if (paid <= 0) return { key: "unpaid", label: "Unpaid", color: "#EF4444" };
  if (paid >= total) return { key: "fully_paid", label: "Fully Paid", color: "#10B981" };
  return { key: "deposit_paid", label: "Deposit Paid", color: "#F59E0B" };
}

// Download an official order document (invoice/receipt) as a blob and trigger save
export async function downloadOrderDocument(orderId, doc) {
  const r = await api.get(`/orders/${orderId}/documents/${doc.id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = doc.original_filename || `${doc.doc_type}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// Navigation deep links — use exact coordinates when the order has them, else the address text
export function gmapsNavUrl(order) {
  const dest = order?.event_lat != null && order?.event_lng != null
    ? `${order.event_lat},${order.event_lng}`
    : encodeURIComponent(order?.event_location || "");
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
}
export function wazeNavUrl(order) {
  if (order?.event_lat != null && order?.event_lng != null)
    return `https://waze.com/ul?ll=${order.event_lat},${order.event_lng}&navigate=yes`;
  return `https://waze.com/ul?q=${encodeURIComponent(order?.event_location || "")}&navigate=yes`;
}

export function whatsappLink(phone, message) {
  const clean = (phone || "").replace(/[^\d]/g, "");
  return `https://wa.me/${clean}?text=${encodeURIComponent(message || "")}`;
}

// Returns the full list of roles a user holds (handles legacy single-role docs).
export function userRoles(user) {
  if (!user) return [];
  if (Array.isArray(user.roles) && user.roles.length > 0) return user.roles;
  if (user.role) return [user.role];
  return [];
}

// True if the user holds ANY of the supplied roles.
export function hasAnyRole(user, allowed) {
  if (!user || !allowed || allowed.length === 0) return false;
  const have = userRoles(user);
  return allowed.some((r) => have.includes(r));
}

// Returns a customer-facing WhatsApp message template for a given order stage.
// Each template is concise, personalised with the customer name, package and tracking URL.
export function stageWhatsAppTemplate(stage, order, trackUrl) {
  const name = order?.customer_name || "there";
  const pkg = order?.package_name || "your event";
  const link = trackUrl || "";
  const date = order?.event_date
    ? new Date(order.event_date).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" })
    : "";
  const map = {
    order_confirmed: {
      label: "Order confirmed",
      message: `Hi ${name}, your booking for "${pkg}" on ${date} is CONFIRMED. We'll keep you updated. Track live: ${link}`,
    },
    kitchen_preparing: {
      label: "Kitchen preparing",
      message: `Hi ${name}, our chefs have started preparing your menu for "${pkg}". Everything is on schedule. Track: ${link}`,
    },
    driver_assigned: {
      label: "Driver assigned",
      message: `Hi ${name}, a driver has been assigned for your "${pkg}". You'll be notified once they depart. Track: ${link}`,
    },
    on_the_way: {
      label: "Driver on the way",
      message: `Hi ${name}, our driver is now ON THE WAY to your event "${pkg}". Live tracking: ${link}`,
    },
    setup_in_progress: {
      label: "Setup in progress",
      message: `Hi ${name}, our team has arrived and is setting up "${pkg}". Track progress: ${link}`,
    },
    event_ready: {
      label: "Event ready",
      message: `Hi ${name}, your "${pkg}" is fully set up and READY for your guests. Enjoy the celebration!`,
    },
    completed: {
      label: "Event completed",
      message: `Hi ${name}, thank you for choosing HNC for "${pkg}". Your event is complete — we'd love your feedback!`,
    },
  };
  return map[stage] || { label: stage, message: `Hi ${name}, status update: ${stage.replace(/_/g, " ")}. Track: ${link}` };
}

export function stageLabel(stage, t) {
  const map = {
    order_confirmed: t("stage.order_confirmed", "Order Confirmed"),
    kitchen_preparing: t("stage.kitchen_preparing", "Kitchen Preparing"),
    driver_assigned: t("stage.driver_assigned", "Driver Assigned"),
    on_the_way: t("stage.on_the_way", "On The Way"),
    setup_in_progress: t("stage.setup_in_progress", "Setup In Progress"),
    event_ready: t("stage.event_ready", "Event Ready"),
    completed: t("stage.completed", "Completed"),
  };
  return map[stage] || stage;
}

export const STAGES = [
  "order_confirmed",
  "kitchen_preparing",
  "driver_assigned",
  "on_the_way",
  "setup_in_progress",
  "event_ready",
  "completed",
];

export function fmtCurrency(n) {
  const v = Number(n || 0);
  return `RM ${v.toLocaleString("en-MY", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export function fmtDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-MY", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
